import { useLocalObservable } from "mobx-react-lite";
import { createContext, ReactNode, useContext, useEffect } from "react";

import { SheetStore } from "./sheet-store";

const SheetContext = createContext<SheetStore | null>(null);

interface SheetProviderProps<S extends SheetStore = SheetStore> {
  store?: S;
  children: ReactNode;
}

export const SheetProvider = <S extends SheetStore = SheetStore>({
  store: externalStore,
  children,
}: SheetProviderProps<S>) => {
  const internalStore = useLocalObservable(() => new SheetStore());
  const store = externalStore ?? internalStore;
  const isExternalStore = externalStore !== undefined;

  useEffect(() => {
    if (isExternalStore) {
      return;
    }
    return () => {
      store.cleanup();
    };
  }, [store, isExternalStore]);

  return <SheetContext.Provider value={store}>{children}</SheetContext.Provider>;
};

export const useSheetStore = (): SheetStore => {
  const store = useContext(SheetContext);

  if (!store) {
    throw new Error("useSheetStore must be used within a <SheetProvider>");
  }

  return store;
};
